import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Heart, 
  Envelope,
  Phone,
  ArrowLeft,
  CheckCircle
} from '@phosphor-icons/react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setSubmitted(true);
  };

  return (
    <PageLayout showFooter={false}>
      <div className="min-h-screen rose-gradient flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <Heart weight="fill" className="w-10 h-10 text-primary" />
              <Heart weight="fill" className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="script-font text-3xl text-primary">Link, Laugh & Love</h1>
            <p className="text-muted-foreground text-sm mt-1">Password Recovery</p>
          </div>

          {/* Card */}
          <div className="paper-card rounded-lg p-8 shadow-xl">
            {!submitted ? (
              <>
                <h2 className="font-serif text-2xl text-center mb-2">Forgot Password?</h2>
                <p className="text-muted-foreground text-center text-sm mb-8">
                  No worries! Enter your email below and our team will help you reset your password.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="email" className="form-label">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="form-input"
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button type="submit" className="btn-primary w-full" data-testid="submit-forgot-btn">
                    <Envelope className="w-4 h-4 mr-2" />
                    Request Password Reset
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login" className="text-primary text-sm hover:underline inline-flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle weight="fill" className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-serif text-2xl mb-2">Request Received!</h2>
                <p className="text-muted-foreground mb-6">
                  Our team has been notified about your password reset request for <strong>{email}</strong>.
                </p>
                
                <div className="bg-pink-50 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-gray-800 mb-2">What happens next?</h3>
                  <p className="text-muted-foreground text-sm">
                    Our team will contact you within 24 hours to verify your identity and help you reset your password.
                  </p>
                </div>

                <div className="border-t border-border pt-6">
                  <p className="text-muted-foreground text-sm mb-3">Need immediate help?</p>
                  <div className="flex flex-col gap-2">
                    <a href="mailto:LinkLaughLoveMatch@gmail.com" className="text-primary hover:underline inline-flex items-center justify-center gap-2">
                      <Envelope className="w-4 h-4" />
                      LinkLaughLoveMatch@gmail.com
                    </a>
                    <a href="tel:8107991060" className="text-primary hover:underline inline-flex items-center justify-center gap-2">
                      <Phone className="w-4 h-4" />
                      (810) 799-1060
                    </a>
                  </div>
                </div>

                <div className="mt-6">
                  <Link to="/login">
                    <Button variant="outline" className="btn-secondary">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ForgotPassword;
